import type { FilterPayloadNode, GridFilterOperator } from '@zoryn/components/grid'
import type { RuleContentType } from './rules'
import { RULE_ERROR_MESSAGE_KEY, RULE_RESULT_VALUE_KEY } from './rules'

export type RuleScriptLanguage = 'aviator'

export interface RuleBindingSavePayload {
  ruleName?: string
  priority?: number
  enabled?: boolean
  rulesJson: FilterPayloadNode | FilterPayloadNode[] | null
  resultJson: Record<string, unknown>
  ruleScript: string
  ruleExpr: string
  scriptLanguage: RuleScriptLanguage
}

export function formatAviatorValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'nil'
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }
  if (typeof value === 'number') {
    return String(value)
  }
  return `'${String(value).replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
}

function compileAviatorField(
  field: string,
  operator: GridFilterOperator,
  value: unknown
): string {
  switch (operator) {
    case 'gt':
      return `${field} > ${formatAviatorValue(value)}`
    case 'ge':
      return `${field} >= ${formatAviatorValue(value)}`
    case 'lt':
      return `${field} < ${formatAviatorValue(value)}`
    case 'le':
      return `${field} <= ${formatAviatorValue(value)}`
    case 'eq':
    case '=':
      return `${field} == ${formatAviatorValue(value)}`
    case 'ne':
      return `${field} != ${formatAviatorValue(value)}`
    case 'like':
      return `string.contains(${field}, ${formatAviatorValue(value)})`
    case 'notLike':
      return `!string.contains(${field}, ${formatAviatorValue(value)})`
    case 'empty':
      return `(${field} == nil || ${field} == '')`
    case 'notEmpty':
      return `(${field} != nil && ${field} != '')`
    default:
      return 'false'
  }
}

export function compileAviatorCondition(node: FilterPayloadNode): string {
  if (node.type === 'group' && node.children?.length) {
    return compileAviatorGroup(node.children)
  }
  if (node.field && node.operator) {
    return compileAviatorField(node.field, node.operator, node.value)
  }
  if (node.children?.length) {
    return compileAviatorGroup(node.children)
  }
  return 'true'
}

export function compileAviatorGroup(nodes: FilterPayloadNode[]): string {
  if (!nodes.length) {
    return 'true'
  }
  if (nodes.length === 1) {
    return compileAviatorCondition(nodes[0])
  }
  const parts = nodes.map((node, index) => {
    const expr = compileAviatorCondition(node)
    if (index === 0) {
      return expr
    }
    const logic = node.logic === 'or' ? '||' : '&&'
    return `${logic} ${expr}`
  })
  return `(${parts.join(' ')})`
}

export function compileAviatorScript(
  rulesJson: FilterPayloadNode | FilterPayloadNode[] | null
): string {
  if (rulesJson == null) {
    return 'true'
  }
  if (Array.isArray(rulesJson)) {
    if (!rulesJson.length) {
      return 'true'
    }
    if (rulesJson.length === 1) {
      return compileAviatorCondition(rulesJson[0])
    }
    return compileAviatorGroup(rulesJson)
  }
  return compileAviatorCondition(rulesJson)
}

function formatReadableValue(value: unknown): string {
  if (typeof value === 'string') {
    return `'${value}'`
  }
  if (typeof value === 'boolean') {
    return value ? '开' : '关'
  }
  return String(value ?? '')
}

const OPERATOR_READABLE: Partial<Record<GridFilterOperator, string>> = {
  gt: '>',
  ge: '>=',
  lt: '<',
  le: '<=',
  eq: '=',
  '=': '=',
  ne: '!=',
  like: 'like',
  notLike: 'not like',
  empty: 'is empty',
  notEmpty: 'is not empty',
}

function readableCondition(node: FilterPayloadNode): string {
  if (node.type === 'group' && node.children?.length) {
    return `(${readableGroup(node.children)})`
  }
  if (node.field && node.operator) {
    const op = OPERATOR_READABLE[node.operator] ?? node.operator
    if (node.operator === 'empty' || node.operator === 'notEmpty') {
      return `${node.field} ${op}`
    }
    return `${node.field} ${op} ${formatReadableValue(node.value)}`
  }
  if (node.children?.length) {
    return `(${readableGroup(node.children)})`
  }
  return 'true'
}

function readableGroup(nodes: FilterPayloadNode[]): string {
  return nodes
    .map((node, index) => {
      const text = readableCondition(node)
      if (index === 0) {
        return text
      }
      const logic = node.logic === 'or' ? 'or' : 'and'
      return `${logic} ${text}`
    })
    .join(' ')
}

export function formatRuleExpr(
  rulesJson: FilterPayloadNode | FilterPayloadNode[] | null,
  resultJson?: Record<string, unknown> | null,
  options?: { resultField?: string; content?: RuleContentType }
): string {
  const content = options?.content ?? 'result'

  if (rulesJson == null) {
    if (content === 'boolean') {
      return '（兜底）通过'
    }
    if (content === 'error') {
      const message = resultJson?.[RULE_ERROR_MESSAGE_KEY]
      return message ? `（兜底）error: ${message}` : '（兜底）'
    }
    const resultField = options?.resultField ?? RULE_RESULT_VALUE_KEY
    const resultValue = resultJson?.[resultField]
    return resultValue == null ? '（兜底）' : `（兜底） return ${resultValue}`
  }

  const nodes = Array.isArray(rulesJson) ? rulesJson : [rulesJson]
  const condition =
    nodes.length === 1 ? readableCondition(nodes[0]) : readableGroup(nodes)

  if (content === 'boolean') {
    return `${condition} => 通过`
  }

  if (content === 'error') {
    const message = resultJson?.[RULE_ERROR_MESSAGE_KEY]
    return message ? `${condition} error: ${message}` : condition
  }

  const resultField = options?.resultField ?? RULE_RESULT_VALUE_KEY
  const resultValue = resultJson?.[resultField]

  if (resultValue == null || resultValue === '') {
    return condition
  }
  return `${condition} return ${resultValue}`
}

export function buildRuleBindingPayload(
  rulesJson: FilterPayloadNode | FilterPayloadNode[] | null,
  resultJson: Record<string, unknown>,
  options?: {
    ruleName?: string
    priority?: number
    enabled?: boolean
    content?: RuleContentType
    resultField?: string
  }
): RuleBindingSavePayload {
  const content = options?.content ?? 'result'
  const normalizedResultJson =
    content === 'boolean' ? {} : { ...resultJson }

  return {
    ruleName: options?.ruleName,
    priority: options?.priority,
    enabled: options?.enabled ?? true,
    rulesJson,
    resultJson: normalizedResultJson,
    ruleScript: compileAviatorScript(rulesJson),
    ruleExpr: formatRuleExpr(rulesJson, normalizedResultJson, {
      resultField: options?.resultField,
      content,
    }),
    scriptLanguage: 'aviator',
  }
}
